import React from 'react';
import { StyleSheet, View } from 'react-native';

import { ConfirmDeleteDialogButton } from '@components/Dialog';
import { FormSectionTitle, useAppForm } from '@components/Form';
import ScreenContainer from '@components/ScreenContainer';
import { useGroups } from '@context/GroupsContext';
import { useRecommendations } from '@context/RecommendationsContext';
import { useAppTheme } from '@context/ThemeContext';
import { validatePlace } from '@db/Places';
import {
  useDeletePlanMutation,
  usePlanInviteIdsQuery,
  usePlanQuery,
  useUpdatePlanMutation,
} from '@db/Plans/Queries';
import { errorAlert } from '@helpers/Alert';
import { validateDateRange } from '@helpers/Dates';
import { resetToHomeWithReload } from '@navigation/Actions';
import { createAppNavigation } from '@navigation/AppNavigation';
import { UpdatePlanScreenProps } from '@navigation/Types';
import { DateRange, Photo, Place } from '@types';

type UpdatePlanFormValues = {
  dateRange: DateRange;
  description: string;
  joinsRequested: boolean;
  location: Place | undefined;
  notes: string;
  photos: Photo[];
  selectedGroupIds: Set<string>;
  selectedPeopleIds: Set<string>;
};

export function UpdatePlanScreen({ navigation, route }: UpdatePlanScreenProps) {
  const appNavigation = React.useMemo(() => createAppNavigation(navigation), [navigation]);
  const { recommendedInvitePeople } = useRecommendations();
  const { userGroups } = useGroups();

  const appTheme = useAppTheme();
  const planId = route.params?.planId;
  const planQuery = usePlanQuery({ planId });
  const planInviteIdsQuery = usePlanInviteIdsQuery({ planId });
  const initializedPlanIdRef = React.useRef<string | undefined>(undefined);
  const initializedInvitePlanIdRef = React.useRef<string | undefined>(undefined);
  const updatePlanMutation = useUpdatePlanMutation();
  const deletePlanMutation = useDeletePlanMutation();

  const defaultValues: UpdatePlanFormValues = {
    dateRange: {
      startDate: undefined,
      endDate: undefined,
    },
    description: '',
    joinsRequested: false,
    location: undefined,
    notes: '',
    photos: [],
    selectedGroupIds: new Set<string>(),
    selectedPeopleIds: new Set<string>(),
  };

  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      const location = value.location;
      const { startDate, endDate } = value.dateRange;
      if (!location || !startDate || !endDate) {
        throw new Error('Update plan form submitted without required details.');
      }

      try {
        await updatePlanMutation.mutateAsyncOrThrow({
          planId,
          plan: {
            location,
            description: value.description,
            startDate,
            endDate,
            notes: value.notes,
            photos: value.photos,
            joinsRequested: value.joinsRequested,
          },
          groupIds: [...value.selectedGroupIds],
          people: recommendedInvitePeople.filter((person) =>
            value.selectedPeopleIds.has(person.personId)
          ),
        });
        appNavigation.openPlan(planId, { reload: true });
      } catch (error) {
        errorAlert(error);
        throw error;
      }
    },
  });

  React.useEffect(() => {
    const plan = planQuery.plan;
    if (!plan || initializedPlanIdRef.current === plan.planId) return;

    initializedPlanIdRef.current = plan.planId;
    form.setFieldValue('location', plan.location);
    form.setFieldValue('dateRange', {
      startDate: plan.startDate,
      endDate: plan.endDate,
    });
    form.setFieldValue('description', plan.description || '');
    form.setFieldValue('photos', plan.photos);
    form.setFieldValue('notes', plan.notes || '');
    form.setFieldValue('selectedGroupIds', new Set(plan.groupIds ?? []));
    form.setFieldValue('joinsRequested', Boolean(plan.joinsRequested));
  }, [form, planQuery.plan]);

  React.useEffect(() => {
    if (!planId || planInviteIdsQuery.loading || initializedInvitePlanIdRef.current === planId) {
      return;
    }

    initializedInvitePlanIdRef.current = planId;
    // TODO: load contact phone numbers to match selected people across devices
    // const contactPhoneNumbers = await getPlanContactPhoneNumbers(planId);
    // setSelectedContactPhoneNumbers(new Set(contactPhoneNumbers));
    form.setFieldValue('selectedPeopleIds', new Set(planInviteIdsQuery.inviteIds));
  }, [form, planId, planInviteIdsQuery.inviteIds, planInviteIdsQuery.loading]);

  const onPressDelete = async () => {
    try {
      if (planId) {
        await deletePlanMutation.mutateAsyncOrThrow({ planId });
        resetToHomeWithReload(navigation);
      }
    } catch (error) {
      errorAlert(error);
    }
  };

  const renderLocationComponent = () => (
    <View style={styles.inputContainer}>
      <FormSectionTitle icon="map-marker-outline" title="Location" />
      <form.AppField
        name="location"
        validators={{
          onChange: ({ value }) => validatePlace(value) || undefined,
          onSubmit: ({ value }) => validatePlace(value) || undefined,
        }}>
        {(field) => (
          <field.PlaceAutocomplete
            types="(cities)"
            placeholder="Enter a destination"
            testID="components.PlanForm.LocationInput"
          />
        )}
      </form.AppField>
    </View>
  );

  const renderDateComponent = () => (
    <View style={styles.inputContainer}>
      <FormSectionTitle icon="calendar-outline" title="Dates" />
      <form.AppField
        name="dateRange"
        validators={{
          onChange: ({ value }) => validateDateRange(value) || undefined,
          onSubmit: ({ value }) => validateDateRange(value) || undefined,
        }}>
        {(field) => <field.DateRangeInput testID="components.PlanForm.DateInput" />}
      </form.AppField>
    </View>
  );

  const renderPhotosComponent = () => (
    <View>
      <View style={styles.inputContainer}>
        <FormSectionTitle icon="image-outline" title="Photos" />
      </View>
      <form.AppField name="photos">
        {(field) => <field.PhotoListInput testID="components.PlanForm.PhotoInput" />}
      </form.AppField>
    </View>
  );

  const renderNotesComponent = () => (
    <View style={styles.inputContainer}>
      <View>
        <FormSectionTitle icon="text" title="Notes" />
      </View>
      <form.AppField name="notes">
        {(field) => (
          <field.TextInput
            placeholder="Add details"
            multiline
            numberOfLines={4}
            style={styles.notesInput}
            testID="components.PlanForm.NotesInput"
          />
        )}
      </form.AppField>
    </View>
  );

  const renderGroupsComponent = () => (
    <View>
      <View style={styles.inputContainer}>
        <FormSectionTitle icon="account-multiple" title="Groups" />
      </View>
      <form.AppField name="selectedGroupIds">
        {(field) => (
          <field.GroupPickerInput
            groups={userGroups}
            style={styles.inputContainer}
            testID="components.PlanForm.GroupInput"
          />
        )}
      </form.AppField>
    </View>
  );

  const renderPeopleComponent = () => (
    <View>
      <View style={styles.inputContainer}>
        <FormSectionTitle icon="account" title="People" />
      </View>
      <form.AppField name="selectedPeopleIds">
        {(field) => (
          <form.Subscribe selector={(state) => state.values.joinsRequested}>
            {(joinsRequested) => (
              <field.PersonPickerInput
                people={recommendedInvitePeople}
                joinsRequested={joinsRequested}
                onJoinsRequestedChange={(nextJoinsRequested) =>
                  form.setFieldValue('joinsRequested', nextJoinsRequested)
                }
                style={styles.inputContainer}
                testID="components.PlanForm.PeopleInput"
              />
            )}
          </form.Subscribe>
        )}
      </form.AppField>
    </View>
  );

  const renderUpdateButton = () => (
    <View style={styles.inputContainer}>
      <form.SubmitBigButton mode="contained" testID="screens.UpdatePlanScreen.Button.Update">
        Update
      </form.SubmitBigButton>
    </View>
  );

  const renderDeleteButton = () => (
    <View style={styles.inputContainer}>
      <ConfirmDeleteDialogButton
        onDelete={onPressDelete}
        mode="contained"
        message="This will permanently delete your plan and can't be undone."
        buttonColor={appTheme.colors.error}
        testID="screens.UpdatePlanScreen.Button.Delete">
        Delete
      </ConfirmDeleteDialogButton>
    </View>
  );

  return (
    <form.AppForm>
      <ScreenContainer
        scrollable
        loading={planQuery.loading}
        error={planQuery.error}
        errorProps={planQuery.errorProps}
        testID="screens.UpdatePlanScreen.Background">
        {renderLocationComponent()}
        {renderDateComponent()}
        {renderPeopleComponent()}
        {renderGroupsComponent()}
        {renderNotesComponent()}
        {renderPhotosComponent()}
        {renderUpdateButton()}
        {renderDeleteButton()}
      </ScreenContainer>
    </form.AppForm>
  );
}

const styles = StyleSheet.create({
  inputContainer: {
    marginHorizontal: 20,
  },
  notesInput: {
    height: 100,
    marginVertical: 5,
  },
});
